// src/oura/lib/timelineExport.ts
//
// Generates the client-facing Execution Timeline as a self-contained HTML
// document and downloads it, modeled on the OT-2026-BCO-001 deliverable:
// header block, week by week plan with checkmarks, the scoreboard, and the
// commitment footer. Print to PDF from the browser gives the client PDF.

import type { Business } from "../contract";
import type { BusinessTracker } from "../tracker";

const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function downloadExecutionTimeline(business: Business, tracker: BusinessTracker): void {
    const weeksHtml = tracker.weeks
        .map(
            (w) => `
      <section class="week">
        <div class="week-head">
          <span class="badge">${w.week}</span>
          <div>
            <p class="eyebrow">WEEK ${w.week} · ${esc(w.title.toUpperCase())} · ${esc(w.dates)}</p>
            <h2>${esc(w.goal)}</h2>
          </div>
        </div>
        <ul>
          ${w.tasks.map((t) => `<li><span class="check">✓</span><div><b>${esc(t.action)}.</b> ${esc(t.notes)} <span class="gate">Verified when: ${esc(t.gate.toLowerCase())}.</span></div></li>`).join("")}
        </ul>
        <p class="deliverable"><span>DELIVERABLE</span> ${esc(w.deliverable)}</p>
      </section>`,
        )
        .join("");

    const scoreboardHtml = tracker.scoreboard
        .map(
            (f) => `
        <tr>
          <td>${esc(f.label)}</td>
          <td>${esc(f.baseline)}</td>
          <td>${esc(f.target)}</td>
          <td class="${f.result ? "" : "fill"}">${f.result ? esc(f.result) : "Filled at handover"}</td>
        </tr>`,
        )
        .join("");

    const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(business.name)} · Execution Timeline · ${esc(tracker.reference)}</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; margin: 0; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1f2937; background: #fff; padding: 48px; max-width: 860px; margin: 0 auto; line-height: 1.55; }
  header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 28px; }
  .brand { display: flex; align-items: center; gap: 8px; font-weight: 700; color: #0B49FE; font-size: 18px; }
  .brand .mark { display: inline-flex; width: 26px; height: 26px; border-radius: 50%; background: #0B49FE; color: #fff; align-items: center; justify-content: center; font-size: 13px; }
  header .tag { font-size: 11px; letter-spacing: .18em; color: #0B49FE; font-weight: 700; }
  h1 { font-size: 28px; color: #111827; margin-bottom: 2px; }
  .sub { color: #0B49FE; font-weight: 600; margin-bottom: 18px; }
  .meta { border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; padding: 12px 0; margin-bottom: 24px; font-size: 14px; }
  .meta div { display: flex; gap: 16px; padding: 3px 0; }
  .meta dt { width: 110px; font-size: 10px; letter-spacing: .14em; color: #9ca3af; font-weight: 700; padding-top: 3px; }
  .week { margin-bottom: 26px; }
  .week-head { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 8px; }
  .badge { flex: none; width: 26px; height: 26px; border-radius: 50%; background: #0B49FE; color: #fff; font-size: 13px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; margin-top: 2px; }
  .eyebrow { font-size: 10px; letter-spacing: .16em; color: #0B49FE; font-weight: 700; }
  h2 { font-size: 16px; color: #111827; margin-top: 2px; }
  .week ul { list-style: none; padding-left: 38px; margin-top: 6px; }
  .week li { display: flex; gap: 8px; font-size: 13.5px; padding: 4px 0; }
  .check { color: #059669; font-weight: 700; flex: none; }
  .gate { color: #6b7280; }
  .deliverable { margin: 8px 0 0 38px; font-size: 12.5px; }
  .deliverable span { background: #ecfdf5; color: #047857; font-size: 10px; font-weight: 700; letter-spacing: .1em; padding: 3px 8px; border-radius: 4px; margin-right: 8px; }
  h3 { font-size: 13px; letter-spacing: .12em; color: #0B49FE; margin: 30px 0 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { background: #1e3a5f; color: #fff; text-align: left; padding: 8px 10px; font-size: 11px; letter-spacing: .06em; }
  td { border: 1px solid #e5e7eb; padding: 8px 10px; vertical-align: top; }
  td.fill { background: #fef9e7; color: #92400e; }
  .box { border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px 20px; margin-top: 26px; font-size: 13.5px; }
  .box b { color: #111827; }
  footer { margin-top: 34px; padding-top: 12px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; color: #9ca3af; font-size: 11.5px; }
  @media print { body { padding: 24px; } }
</style>
</head>
<body>
  <header>
    <span class="brand"><span class="mark">O</span> ourteam</span>
    <span class="tag">EXECUTION TIMELINE</span>
  </header>

  <h1>${esc(tracker.packageName)}</h1>
  <p class="sub">Execution timeline for ${esc(business.name)}</p>

  <dl class="meta">
    <div><dt>REFERENCE</dt><dd><b>${esc(tracker.reference)}</b></dd></div>
    <div><dt>WINDOW</dt><dd>${esc(tracker.window)}</dd></div>
    <div><dt>PREPARED BY</dt><dd>ourteam (TEAM AI Sdn Bhd) · hello@ourteam.ai</dd></div>
  </dl>

  ${weeksHtml}

  <h3>THE SCOREBOARD · SIX FACTORS MEASURED BEFORE AND AFTER</h3>
  <table>
    <thead><tr><th>Factor</th><th>Baseline</th><th>Target by handover</th><th>Handover result</th></tr></thead>
    <tbody>${scoreboardHtml}</tbody>
  </table>

  <div class="box">
    <p><b>Our commitment.</b> Fixed price, fixed scope, no surprises. Every item above is delivered and
    live, with one revision round included. Work beyond the stated caps is quoted separately first,
    never billed quietly.</p>
    <p style="margin-top:8px"><b>You are never chased for work,</b> only for two things: access at the
    start, and one copy review in week 2.</p>
  </div>

  <footer>
    <span>TEAM AI Sdn Bhd · ourteam.ai</span>
    <span>Execution timeline · ${esc(tracker.reference)}</span>
  </footer>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ourteam-execution-timeline-${business.domain.replace(/[^a-z0-9.-]/gi, "-")}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
